import { Fragment, useEffect, useRef, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { ActionButton } from '../../../../components/buttons/ActionButton';
import { groupVeralliaDashboardDetections } from '../../../../services/dashboard/verallia-dashboard-grouping-service';
import {
  formatVeralliaDashboardDateTime,
  getVeralliaDashboardDefectLabel,
  sortVeralliaDashboardDetections,
} from '../../../../services/dashboard/verallia-dashboard-service';
import { VeralliaDashboardDetectionImage, VeralliaDashboardNoData } from './VeralliaDashboardBlocks';

const PAGE_SIZE = 25;

const TABLE_COLUMNS = [
  'Fecha de detección',
  'Fecha de fabricación',
  'Equipo',
  'Lote',
  'Línea',
  'Número palet',
  'Tipo de defecto',
  'Tamaño (cm)',
];

const formatManufacturingDate = (date) => {
  if (!date) return '—';

  const parts = date.split('-');
  if (parts.length !== 3) return date;

  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

export const VeralliaDashboardDetectionsTable = ({ detections, page, onPageChange, jump }) => {
  const [expandedIds, setExpandedIds] = useState([]);
  const scrollRef = useRef(null);
  const rowRefs = useRef(new Map());
  const hasFlashedRef = useRef(false);

  const groupedDetections = groupVeralliaDashboardDetections(detections);
  const sortedDetections = sortVeralliaDashboardDetections(groupedDetections);
  const totalPages = Math.max(1, Math.ceil(sortedDetections.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageDetections = sortedDetections.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  useEffect(() => {
    if (!jump?.groupId || hasFlashedRef.current) return;
    const container = scrollRef.current;
    const target = rowRefs.current.get(jump.groupId);
    if (!container || !target) return;

    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const top = container.scrollTop + targetRect.top - containerRect.top - (container.clientHeight - targetRect.height) / 2;
    container.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    container.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });

    const timeout = setTimeout(() => {
      hasFlashedRef.current = true;
      const ids = new Set(jump.highlightIds ?? [jump.groupId]);
      ids.forEach((id) => {
        const row = rowRefs.current.get(id);
        if (!row) return;
        const original = window.getComputedStyle(row).backgroundColor;
        row.animate([
          { backgroundColor: original, offset: 0 },
          { backgroundColor: 'rgba(59, 130, 246, 0.7)', offset: 0.2 },
          { backgroundColor: 'rgba(59, 130, 246, 0.35)', offset: 0.55 },
          { backgroundColor: original, offset: 1 },
        ], { duration: 1400, easing: 'ease-out', iterations: 1 });
      });
    }, 450);

    return () => clearTimeout(timeout);
  }, [jump?.groupId, safePage]);

  const toggleDetection = (detectionId) => {
    setExpandedIds((currentIds) => {
      return currentIds.includes(detectionId)
        ? currentIds.filter((id) => id !== detectionId)
        : [...currentIds, detectionId];
    });
  };

  return (
    <div className="overflow-hidden rounded-md border border-perception-black-500">
      <div ref={scrollRef} className="max-h-[620px] overflow-auto">
        <table className="min-w-full">
          <thead className="sticky top-0 z-[1] bg-perception-black-700">
          <tr className="border-b border-perception-black-500">
            <th className="w-10 px-3 py-3"/>

            {TABLE_COLUMNS.map((column) => (
              <th key={column} className="px-4 py-3 text-left text-xs font-semibold uppercase text-perception-gray-500">
                {column}
              </th>
            ))}
          </tr>
          </thead>

          <tbody>
          {pageDetections.map((detection) => {
            const isExpanded = expandedIds.includes(detection.id);

            return (
              <Fragment key={detection.id}>
                <tr
                  ref={(node) => { if (node) rowRefs.current.set(detection.id, node); else rowRefs.current.delete(detection.id); }}
                  className={`cursor-pointer border-b border-perception-black-500 ${jump && (jump.highlightIds ?? [jump.groupId]).includes(detection.id) ? 'bg-blue-900/20 hover:bg-blue-900/30' : 'bg-perception-black-900 hover:bg-perception-black-700'}`}
                  onClick={() => toggleDetection(detection.id)}
                >
                  <td className="px-3 py-3 text-perception-gray-600">
                    {isExpanded ? <ChevronDownIcon className="size-4"/> : <ChevronRightIcon className="size-4"/>}
                  </td>

                  <td className="whitespace-nowrap px-4 py-3 text-sm text-perception-gray-300">
                    {formatVeralliaDashboardDateTime(detection.timestamp)}
                  </td>

                  <td className="whitespace-nowrap px-4 py-3 text-sm text-perception-gray-300">
                    {formatManufacturingDate(detection.fechaFabricacion)}
                  </td>

                  <td className="px-4 py-3 text-sm text-perception-gray-300">{detection.shift ?? '—'}</td>
                  <td className="px-4 py-3 text-sm text-perception-gray-300">{detection.lote ?? '—'}</td>
                  <td className="px-4 py-3 text-sm text-perception-gray-300">{detection.linea ?? '—'}</td>
                  <td className="px-4 py-3 text-sm text-perception-gray-300">{detection.numeroPalet ?? '—'}</td>

                  <td className="px-4 py-3 text-sm text-perception-gray-300">
                    <div className="flex items-center gap-2">
                      <span>{getVeralliaDashboardDefectLabel(detection.defect)}</span>
                      {detection.detectionCount > 1 && (
                        <span className="rounded bg-perception-black-500 px-2 py-0.5 text-xs text-white">
                          ×{detection.detectionCount}
                        </span>
                      )}
                    </div>
                  </td>

                  <td className="px-4 py-3 text-sm text-perception-gray-300">
                    {detection.sizes.map((size) => Number.isFinite(size) ? size.toFixed(1) : '—').join(' / ')}
                  </td>
                </tr>

                {isExpanded && (
                  <tr className="border-b border-perception-black-500 bg-perception-black-800">
                    <td colSpan={TABLE_COLUMNS.length + 1} className="p-4">
                      <div className="flex flex-col gap-3">
                        <div className="text-xs font-semibold uppercase text-perception-gray-600">Imagen de cámara</div>

                        <VeralliaDashboardDetectionImage
                          src={detection.image}
                          sources={detection.imageSources}
                          alt={detection.numeroPalet ?? detection.id}
                          className="aspect-video w-72"
                        />
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}

          {pageDetections.length === 0 && (
            <tr>
              <td colSpan={TABLE_COLUMNS.length + 1}>
                <VeralliaDashboardNoData/>
              </td>
            </tr>
          )}
          </tbody>
        </table>
      </div>

      {sortedDetections.length > 0 && (
        <div className="flex items-center justify-end gap-3 border-t border-perception-black-500 bg-perception-black-700 px-4 py-3">
          <ActionButton text="Anterior" color="secondary" size="sm" disabled={safePage <= 1} onClick={() => onPageChange(safePage - 1)}/>

          <div className="text-xs uppercase text-perception-gray-600">
            Página {safePage} / {totalPages}
          </div>

          <ActionButton text="Siguiente" color="secondary" size="sm" disabled={safePage >= totalPages} onClick={() => onPageChange(safePage + 1)}/>
        </div>
      )}
    </div>
  );
};
